import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import Box from '@material-ui/core/Box';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import FormLabel from '@material-ui/core/FormLabel';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { HttpsOutlined } from '@material-ui/icons';
import { useDataProvider, useVersion } from 'react-admin';
import { FORM_ERROR } from 'final-form';
import { Form } from 'react-final-form';
import { get, isEqual } from 'lodash';

import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useLocation } from 'react-router-dom';

import { Group, GroupContent, GroupItem, GroupTitle } from 'components/common/Group';
import { LabeledPriceField } from 'components/fields';
import { CountriesSelect, Input, StateSelect, Stripe } from 'components/form';
import { required } from 'components/form/validationRules';
import { ToLocation, PoweredByStripe } from 'icons';
import formatPrice from 'services/helpers/formatPrice';
import getAll from 'services/helpers/getAll';
import SavedCards from '../SavedCards';
import NewCardButton from '../NewCardButton';
import SinglePayAmount from '../SinglePayAmount';

import useCustomNotify from 'hooks/useCustomNotify';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { getDomesticCountryCode } from 'services/helpers/countryCode';

import useProfile from 'hooks/useProfile';
import SubmitButton from 'components/buttons/SubmitButton';
import AddressDistributor from 'components/form/AddressDistributor';
import AutoRefillTab from '../AutoRefillTab';

const useStyles = makeStyles((theme) => ({
  calcBlock: {
    padding: theme.spacing(0, 3, 2),
  },

  calcRow: {
    padding: theme.spacing(1, 0),
  },

  calcTotal: {
    fontWeight: 'bold',
    fontSize: 20,
  },

  secureGroup: {
    position: 'relative',
    backgroundColor: theme.palette.secondary.light,
    padding: theme.spacing(1, 3, 3),
    borderRadius: theme.spacing(2),
  },

  secureGroupTitle: {
    backgroundColor: 'transparent',
    padding: theme.spacing(1.5, 0, 0),
    margin: 0,
  },

  tabs: {
    padding: theme.spacing(0, 2),
    marginBottom: '20px',
  },
  dialogBody: {
    maxWidth: '560px',
    margin: '0 auto',
  },
  error: {
    fontSize: 14,
    color: theme.palette.error.main,
    padding: theme.spacing(0, 0, 0.5, 3),
  },
  button: {
    color: theme.palette.secondary.contrastText,
    border: `1px solid #E0E0E0`,
  },
  groupContent: {
    padding: theme.spacing(0, 2),
  },
  addressGroupTitle: {
    margin: 0,
    padding: theme.spacing(1.5, 2),
  },
  errorText: {
    padding: theme.spacing(0, 0, 0.5, 3),
  },
  actions: {
    padding: '32px 24px',

    '& .MuiButton-containedSecondary': {
      backgroundColor: theme.palette.secondary.light,

      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
    },
  },
  dialogButton: {
    textTransform: 'none',
  },
  feeLoaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '0 0 16px',
  },
}));

const AUTO_REFILL_TAB = 'Auto-Refill';
const SINGLE_PAYMENT_TAB = 'Single Payment';
const MANAGE_PAGE = 'Manage';
const ADD_BALANCE_PAGE = 'ADD BALANCE';
const ADD_NEW_CARD_PAGE = 'ADD NEW CARD';
const NEW_CARD = 'NEW_CARD';
const STATUS_CHECK_INTERVAL = 3000;
const PAYMENT_SUCCESSFUL = 'rollo_succeeded';
const TIMEOUT = 15000;

let timeoutId;

interface CardInfo {
  cardId: string;
  brand: string;
  lastDigits: string;
  failedLastTime: boolean;
}

const formatAmount = (amount) =>
  formatPrice(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const requireCard = (value) => {
  if (!value) {
    return 'Required';
  }

  if (value.error) {
    return value.error.message;
  }

  if (!value.complete) {
    return 'Card details are missing or invalid';
  }
};

let debouncedChange;

const Card = ({
  setPageTitle,
  callback,
  paymentOptions,
  paymentOptionsLoading,
  paymentLimits,
  paymentLoader,
  errorAmount,
  setErrorAmount,
  getBillingData,
}: any) => {
  const notify = useCustomNotify();
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const stripe = useStripe();
  const elements = useElements();
  const version = useVersion();
  const location = useLocation();

  const domesticCountryCode = getDomesticCountryCode();

  const { profile } = useProfile();

  const [completed, setCompleted] = useState(false);
  const openSingleTabByDefault = useSelector(
    (state) => state.refill.openSingleTabByDefault,
    shallowEqual
  );
  const isWelcomePage = useSelector((state) => state.common.isWelcomePage, shallowEqual);

  // const [getWarehouse, { data: warehouses }] = useMutation({
  //   type: 'GET_LIST',
  //   resource: 'warehousePresets',
  //   payload: { pagination: { perPage: 1 } },
  // });

  const [pageType, setPageType] = useState(AUTO_REFILL_TAB);
  const [removingCard, setRemovingCard] = useState('');

  const [pickedCard, setPickedCard] = useState('');
  const [autoRefillAmount, setAutoRefillAmount] = useState(100);

  const [defaultCard, setDefaultCard] = useState(NEW_CARD);
  const [autoRefillCard, setAutoRefillCard] = useState(NEW_CARD);
  const [cards, setCards] = useState<CardInfo[] | null>(null);
  const [enabledAutoRefill, setEnabledAutoRefill] = useState(false);
  const [saveCard, setSaveCard] = useState(true);
  const [failedCard, setFailedCard] = useState<string>('');
  const [showParser, setShowParser] = useState(false);
  const [currentAutoRefillStatus, setCurrentAutoRefillStatus] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [settingsLoading, setSettingsLoading] = useState<boolean>(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [warehouse, setWarehouse] = useState<any>(null);
  const [feesLoading, setFeesLoading] = useState<boolean>(false);
  const [feesError, setFeesError] = useState<string>('');

  useEffect(() => {
    const getWarehouse = async () => {
      try {
        const { data: defaultWarehouse } = await dataProvider.get('warehouseDefault');

        if (defaultWarehouse) {
          setWarehouse({
            country: domesticCountryCode,
            line1: defaultWarehouse.addressLine1,
            line2: defaultWarehouse.addressLine2,
            city: defaultWarehouse.city,
            state: defaultWarehouse.stateOrProvince?.code,
            postal_code: defaultWarehouse.postalCode,
          });

          return;
        }

        const { data: warehousePresets } = await dataProvider.getList('warehousePresets', {
          pagination: { perPage: 1 },
        });

        setWarehouse({
          country: domesticCountryCode,
          line1: warehousePresets?.[0]?.addressLine1,
          line2: warehousePresets?.[0]?.addressLine2,
          city: warehousePresets?.[0]?.city,
          state: warehousePresets?.[0]?.stateOrProvince?.code,
          postal_code: warehousePresets?.[0]?.postalCode,
        });
      } catch (error: any) {
        console.error(error?.message ?? error);
      }
    };

    if (callback) {
      getWarehouse();
    }
  }, [callback, dataProvider, domesticCountryCode]);

  const handleData = async (isInitial?) => {
    try {
      setSettingsLoading(true);

      const billingData = await getBillingData();

      setCards(billingData.cards);

      if (billingData.autoRefill.enabled) {
        setAutoRefillAmount(formatAutoRefillAmount(billingData.autoRefill.amount, false));
      }

      const cardAutoRefillEnabled =
        billingData.autoRefill.enabled && billingData.autoRefill.paymentMethodType === 'CARD';

      setAutoRefillCard(billingData.autoRefill.defaultCardId);
      setDefaultCard(billingData.defaultCardId);

      setCurrentAutoRefillStatus(cardAutoRefillEnabled);

      if (isInitial || billingData.cards.length <= 0) {
        setEnabledAutoRefill(cardAutoRefillEnabled);
      }

      if (billingData.autoRefill.enabled) {
        const selectedCardId = billingData.autoRefill.defaultCardId;

        const selectedCard = billingData.cards?.find((card) => card.cardId === selectedCardId);

        setFailedCard(selectedCard?.failedLastTime ? selectedCard?.lastDigits : '');
      } else {
        setFailedCard('');
      }

      return billingData;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load billing settings';
      setSettingsError(errorMessage);
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    handleData(true).catch((error) => {
      console.log('Error:', error);
    });
  }, [version]); //eslint-disable-line

  useEffect(() => {
    if (openSingleTabByDefault) setPageType(SINGLE_PAYMENT_TAB);
  }, [openSingleTabByDefault]);

  const clearData = (enabledAutoRefill) => {
    setPageType(AUTO_REFILL_TAB);
    setPageTitle(MANAGE_PAGE);
    setRemovingCard('');
    setSaveCard(true);
    setPickedCard('');
    setEnabledAutoRefill(
      typeof enabledAutoRefill === 'boolean' ? enabledAutoRefill : currentAutoRefillStatus
    );
    handleData().catch((error) => {
      console.log('Error:', error);
    });
  };

  const resetTimeout = () => {
    clearTimeout(timeoutId);
    timeoutId = null;
  };

  const getPaymentStatus = async (id: string, amount: number) => {
    try {
      const {
        data: { status },
      } = await dataProvider.getNest('paymentSession', { id });

      if (status === PAYMENT_SUCCESSFUL) {
        setLoading(false);
        notify(`${formatPrice(amount)} is added to your account`, 'billing');
        handleClose(currentAutoRefillStatus, true);
        resetTimeout();
      }

      await new Promise((resolve) => setTimeout(resolve, STATUS_CHECK_INTERVAL));

      return timeoutId ? await getPaymentStatus(id, amount) : null;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred while getting payment status';
      notify(errorMessage, 'error');
      throw error;
    }
  };

  const getPaymentReview = async (id: string, amount: number) => {
    try {
      const {
        data: { review, status },
      } = await dataProvider.getOne('paymentReview', { id });

      if (status === PAYMENT_SUCCESSFUL) {
        setLoading(false);
        notify(`${formatPrice(amount)} is added to your account`, 'billing');
        handleClose(currentAutoRefillStatus, true);
        resetTimeout();
        return true;
      }

      if (review) {
        notify(
          'This payment requires a manual review. Please allow up to 1 business day. Check status under Deposit tab.',
          'error'
        );
        handleClose(currentAutoRefillStatus, true);
        setLoading(false);
        resetTimeout();
      }

      return review;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred while getting payment review';
      notify(errorMessage, 'error');
      throw error;
    }
  };

  const setPaymentTimeout = () =>
    setTimeout(() => {
      resetTimeout();
      setLoading(false);
      handleClose(currentAutoRefillStatus, true);
      notify(
        'Payment processing takes more time than usual. Your balance will be updated once payment is received. Please check your balance in a few minutes.',
        'error'
      );
    }, TIMEOUT);

  const makeSinglePayment = async (values) => {
    try {
      if (!stripe || !elements) {
        throw Error("Stripe hasn't been loaded");
      }

      setLoading(true);

      const customAmount = parseFloat(values.customAmount);

      timeoutId = setPaymentTimeout();

      const {
        data: { clientKey, id },
      } = await dataProvider.post('paymentIntents', {
        data: {
          option: values.paymentOption.option,
          billingAddress: {
            ...getAll(values, {
              name: 'name',
              addressLine1: 'address.line1',
              addressLine2: 'address.line2',
              city: 'address.city',
              stateProvinceCode: 'address.state',
              countryCode: 'address.country',
              postalCode: 'address.postal_code',
            }),
            email: profile && profile.email,
            phone: profile && profile.phone_number,
          },
          saveCard: saveCard,
          customAmount,
          paymentMethodType: 'CARD',
        },
      });

      const result = await stripe.confirmCardPayment(clientKey, {
        payment_method: {
          card: elements.getElement(CardElement)!,
          billing_details: {
            name: values.name as string,
            email: profile && profile.email,
            phone: profile && profile.phone_number,
            address: values.address,
          },
        },
      });

      if (result.error) throw result.error;

      const review = await getPaymentReview(id, customAmount);

      if (review) return;

      await getPaymentStatus(id, customAmount);

      return result;
    } catch (error: any) {
      resetTimeout();
      setLoading(false);

      const errorMessage = error?.message ?? 'An error occurred while making single payment';
      notify(errorMessage, 'error');

      return { [FORM_ERROR]: errorMessage };
    }
  };

  const handleRemoveCard = async (cardId, event) => {
    event.stopPropagation();
    setRemovingCard(cardId);
    try {
      await dataProvider.delete('removeCard', { id: cardId });

      if (pickedCard === cardId) {
        setPickedCard('');
      }

      await handleData();
      notify('A card removed!', 'success');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred while removing card';
      notify(errorMessage, 'error');
    }
  };

  const makePaymentBySavedCard = async (values, savedCardId) => {
    try {
      if (!stripe || !elements) {
        throw Error("Stripe hasn't been loaded");
      }

      setLoading(true);

      const customAmount = parseFloat(values.customAmount);

      timeoutId = setPaymentTimeout();

      const {
        data: { clientKey, id },
      } = await dataProvider.post('paymentIntents', {
        data: {
          option: values.paymentOption.option,
          paymentMethodId: savedCardId,
          customAmount,
          paymentMethodType: 'CARD',
        },
      });

      const result = await stripe.confirmCardPayment(clientKey, {
        payment_method: savedCardId,
      });

      if (result.error) throw result.error;

      const review = await getPaymentReview(id, customAmount);

      if (review) return;

      await getPaymentStatus(id, customAmount);

      return result;
    } catch (error: any) {
      resetTimeout();
      setLoading(false);
      const errorMessage = error.message ?? 'An error occurred while making payment by saved card';
      notify(errorMessage, 'error');
      return { [FORM_ERROR]: errorMessage };
    }
  };

  const handleChangeTab = (form) => (event, value) => {
    form.reset(initialValues);
    setPageType(value);
  };

  const handleChooseCard = (cardId) => {
    setPickedCard(cardId);
  };

  const handleClose = (enabledAutoRefill?: boolean, lastCompleted?: boolean) => {
    setErrorAmount(0);

    if (isWelcomePage) {
      setCompleted((prevState) => {
        callback(null, prevState);
        clearData(enabledAutoRefill);
        return false;
      });
      return;
    }

    if (pageType === ADD_NEW_CARD_PAGE && !completed) {
      setPageType(AUTO_REFILL_TAB);
      setPageTitle(MANAGE_PAGE);
      return;
    }

    if (pageType === ADD_BALANCE_PAGE && !completed && !lastCompleted) {
      setPageType(SINGLE_PAYMENT_TAB);
      setPageTitle(MANAGE_PAGE);
      return;
    }

    setCompleted((prevState) => {
      callback(null, prevState);
      clearData(enabledAutoRefill);
      return false;
    });
  };

  const handleAddNewCard = () => {
    setPageTitle(ADD_NEW_CARD_PAGE);
    setPageType(ADD_NEW_CARD_PAGE);
  };

  const changeAutoRefillAmount = (e) => {
    setAutoRefillAmount(e.target.value);
  };

  const formatAutoRefillAmount = (amount, type) => {
    if (!type) {
      return amount.substr(12);
    } else {
      return 'AUTO_REFILL_' + amount;
    }
  };

  const addNewCard = async (values, form) => {
    try {
      if (!stripe || !elements) {
        throw Error("Stripe hasn't been loaded");
      }

      const {
        data: { clientKey },
      } = await dataProvider.getOne('setupIntent', { id: 'CARD' });
      const { setupIntent: data, error: cardSetupError } = await stripe.confirmCardSetup(
        clientKey,
        {
          payment_method: {
            card: elements.getElement(CardElement)!,
            billing_details: {
              name: values.name as string,
              email: profile && profile.email,
              phone: profile && profile.phone_number,
              address: values.address,
            },
          },
        }
      );

      if (cardSetupError) throw cardSetupError;

      const intentId = data!.id;
      await dataProvider.post('setupIntent', {
        data: {
          setupIntentId: intentId,
          source: 'AUTO_REFILL',
          billingAddress: {
            ...getAll(values, {
              name: 'name',
              addressLine1: 'address.line1',
              addressLine2: 'address.line2',
              city: 'address.city',
              stateProvinceCode: 'address.state',
              countryCode: 'address.country',
              postalCode: 'address.postal_code',
            }),
            email: profile && profile.email,
            phone: profile && profile.phone_number,
          },
        },
      });

      notify('A card added!', 'success');

      await handleData();
      setPageType(AUTO_REFILL_TAB);
      form.initialize(initialValues);
      setPageTitle(MANAGE_PAGE);
    } catch (error: any) {
      const errorMessage = error.message ?? 'An error occurred while adding new card';
      notify(errorMessage, 'error');
    }
  };

  const updateAutoRefill = async (enabled, amount, card, isNotify = true) => {
    try {
      if (card?.length > 0) {
        const result = await dataProvider.put('billingSettings', {
          data: {
            autoRefill: {
              enabled: enabled,
              amount: 'AUTO_REFILL_' + amount,
              defaultCardId: card,
              paymentMethodType: 'CARD',
            },
          },
        });

        if (result?.data?.autoRefill) {
          setCurrentAutoRefillStatus(!!result.data.autoRefill.enabled);
        }

        setDefaultCard(card);
        if (isNotify) {
          notify('Auto-Refill setting updated!', 'success');
          handleClose(!!result.data.autoRefill.enabled);
        }
      } else {
        throw new Error('Please add a payment card!');
      }
    } catch (error: any) {
      if (error.message.includes('manual review')) {
        notify(
          'This payment requires a manual review. Please allow up to 1 business day. Check status under Deposit tab.',
          'error'
        );
        handleClose(false);
        return;
      }

      return { [FORM_ERROR]: error.message };
    }
  };

  const handleSubmitForm = (values, form) => {
    setErrorAmount(0);
    if (pageType === SINGLE_PAYMENT_TAB) {
      if (pickedCard === NEW_CARD) {
        setPageType(ADD_BALANCE_PAGE);
        setPageTitle(ADD_BALANCE_PAGE);
        return;
      } else if (pickedCard === '') {
        return makePaymentBySavedCard(values, defaultCard);
      } else {
        return makePaymentBySavedCard(values, pickedCard);
      }
    }
    if (pageType === ADD_BALANCE_PAGE) {
      return makeSinglePayment(values);
    }
    if (pageType === ADD_NEW_CARD_PAGE) {
      return addNewCard(values, form).catch((err) => ({
        [FORM_ERROR]: err.message,
      }));
    }
    if (pageType === AUTO_REFILL_TAB) {
      const autoCard = !pickedCard ? autoRefillCard : pickedCard;
      return updateAutoRefill(enabledAutoRefill, autoRefillAmount, autoCard);
    }
  };

  const initialValues = useMemo(
    () => ({
      paymentOption: null,
      name: null,
      address: warehouse || {},
      customAmount: null,
    }),
    [warehouse]
  );

  if (!callback) {
    return null;
  }

  return (
    <Form
      initialValues={initialValues}
      initialValuesEqual={isEqual}
      onSubmit={handleSubmitForm}
      render={({
        handleSubmit,
        submitting,
        values,
        submitError,
        form,
        initialValues,
        ...renderProps
      }) => {
        const getPaymentFees = async (amount?: number) => {
          try {
            setFeesError('');

            if (!amount || amount < paymentLimits?.min) {
              form.change('paymentOption', {
                option: null,
                baseAmount: amount,
                stripeFee: 0,
                totalAmount: amount ?? 0,
              });
              return;
            }

            const {
              data: { fee, total },
            } = await dataProvider.create('paymentFees', {
              data: {
                amount: amount,
              },
            });

            form.change('paymentOption', {
              option: null,
              baseAmount: amount,
              stripeFee: fee,
              totalAmount: total,
            });
          } catch (error: any) {
            setFeesError(error?.message ?? 'Couldn’t calculate the fee.');
          } finally {
            setFeesLoading(false);
          }
        };

        const handleChangeAmount = async (value) => {
          setFeesError('');
          const convertedValue = parseFloat(value);
          form.change('customAmount', value);
          const matchOption = paymentOptions?.find(
            (option) => option.baseAmount === convertedValue
          );

          if (debouncedChange) {
            clearTimeout(debouncedChange);
            debouncedChange = undefined;
          }

          if (matchOption) {
            form.batch(() => {
              form.change('paymentOption', matchOption);
            });
            return;
          }

          debouncedChange = setTimeout(async () => {
            setFeesLoading(true);
            await getPaymentFees(convertedValue);
          }, 500);
        };

        if (errorAmount && !values.customAmount && typeof values.customAmount !== 'string') {
          handleChangeAmount(errorAmount.toFixed(2));
        }

        const handleFeeErrorFix = () => {
          setFeesLoading(true);
          getPaymentFees(Number(values?.customAmount ?? 0));
        };

        const formSubmitting = submitting || loading || settingsLoading || !!settingsError;

        return (
          <form onSubmit={handleSubmit}>
            {settingsError && (
              <div className={classes.errorText}>
                <Typography variant="body1" color="error">
                  {settingsError}
                </Typography>
              </div>
            )}
            <>
              {!settingsLoading && !settingsError && (
                <div className={classes.dialogBody}>
                  {(pageType === AUTO_REFILL_TAB || pageType === SINGLE_PAYMENT_TAB) && (
                    <Tabs
                      className={classes.tabs}
                      value={pageType}
                      indicatorColor="primary"
                      textColor="primary"
                      onChange={handleChangeTab(form)}>
                      <Tab value={AUTO_REFILL_TAB} label="Auto-Refill" />
                      <Tab value={SINGLE_PAYMENT_TAB} label="Single Payment" />
                    </Tabs>
                  )}
                  {pageType === AUTO_REFILL_TAB && (
                    <AutoRefillTab
                      enabledAutoRefill={enabledAutoRefill}
                      failedCard={failedCard}
                      autoRefillAmount={autoRefillAmount}
                      changeAutoRefillAmount={changeAutoRefillAmount}
                      setEnabledAutoRefill={setEnabledAutoRefill}
                    />
                  )}
                  {pageType === SINGLE_PAYMENT_TAB && (
                    <>
                      {!!errorAmount && location.pathname.includes('/orders/groups') && (
                        <div className={classes.error}>
                          Please add at least {formatPrice(errorAmount)} funds or enable auto-refill
                          to complete this transaction.
                        </div>
                      )}

                      {!paymentOptionsLoading && paymentOptions?.length > 0 && (
                        <SinglePayAmount
                          options={paymentOptions?.map((option) => ({
                            label: formatAmount(option.baseAmount),
                            value: option,
                          }))}
                          form={form}
                          paymentLimits={paymentLimits}
                          values={values}
                          handleChangeAmount={handleChangeAmount}
                          setFeesError={setFeesError}
                        />
                      )}
                      <GroupContent className={classes.calcBlock}>
                        <GroupItem wide>
                          <LabeledPriceField
                            classes={{ root: classes.calcRow }}
                            label="Payment processing fee"
                            record={values}
                            source="paymentOption.stripeFee"
                            info="The fee is paid to the payment processing company and is not
                            collected by Rollo."
                            loading={feesLoading}
                            error={feesError}
                            onErrorFix={handleFeeErrorFix}
                          />
                        </GroupItem>
                        <GroupItem wide>
                          <LabeledPriceField
                            classes={{
                              root: classes.calcRow,
                              price: classes.calcTotal,
                            }}
                            label="Total pay"
                            record={values}
                            source="paymentOption.totalAmount"
                            loading={feesLoading}
                            error={feesError}
                          />
                        </GroupItem>
                      </GroupContent>
                    </>
                  )}
                  {pageType === AUTO_REFILL_TAB && enabledAutoRefill && (
                    <>
                      {cards && (
                        <SavedCards
                          pageType={pageType}
                          cards={cards}
                          defaultCard={autoRefillCard}
                          handleRemoveCard={handleRemoveCard}
                          removing={removingCard}
                          onChange={handleChooseCard}
                          submitError={submitError}
                        />
                      )}
                      <NewCardButton onClick={handleAddNewCard} />
                    </>
                  )}
                  {pageType === SINGLE_PAYMENT_TAB && (
                    <>
                      {cards && (
                        <SavedCards
                          pageType={pageType}
                          cards={cards}
                          defaultCard={defaultCard && cards?.length ? defaultCard : NEW_CARD}
                          handleRemoveCard={handleRemoveCard}
                          removing={removingCard}
                          onChange={handleChooseCard}
                          submitError={submitError}
                        />
                      )}
                    </>
                  )}
                  {(pageType === ADD_BALANCE_PAGE || pageType === ADD_NEW_CARD_PAGE) && (
                    <>
                      <Group>
                        <GroupTitle className={classes.addressGroupTitle} icon={<ToLocation />}>
                          <Grid container justify={'space-between'} alignItems={'flex-start'}>
                            <Grid item>Billing address</Grid>
                            <Grid item>
                              <Button
                                size="small"
                                className={classes.button}
                                endIcon={showParser ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                                onClick={() => setShowParser(!showParser)}>
                                {`Paste ${domesticCountryCode} Address`}
                              </Button>
                            </Grid>
                          </Grid>
                        </GroupTitle>
                        <GroupContent className={classes.groupContent}>
                          {showParser && (
                            <GroupItem wide>
                              <Input
                                name="string"
                                label={`Paste or type ${domesticCountryCode} address here...`}
                                multiline={true}
                              />
                              <AddressDistributor
                                field="string"
                                targets={{
                                  name: 'name',
                                  addressLine1: 'address.line1',
                                  addressLine2: 'address.line2',
                                  city: 'address.city',
                                  stateProvinceCode: 'address.state',
                                  zipCode: 'address.postal_code',
                                }}
                                form={form}
                              />
                            </GroupItem>
                          )}
                          <GroupItem>
                            <Input
                              name="address.line1"
                              label="Address Line 1"
                              validate={required}
                            />
                          </GroupItem>
                          <GroupItem>
                            <Input name="address.line2" label="Address Line 2" />
                          </GroupItem>
                          <GroupItem>
                            <Input name="address.city" label="City / Town" validate={required} />
                          </GroupItem>
                          <GroupItem>
                            <StateSelect
                              label="State"
                              name="address.state"
                              countryCode={get(values, 'address.country')}
                              validate={required}
                            />
                          </GroupItem>
                          <GroupItem>
                            <Input label="ZIP" name="address.postal_code" validate={required} />
                          </GroupItem>
                          <GroupItem>
                            <CountriesSelect
                              label="Country"
                              name="address.country"
                              disabled={true}
                              validate={required}
                            />
                          </GroupItem>
                        </GroupContent>
                      </Group>
                      <Group className={classes.secureGroup}>
                        <GroupTitle className={classes.secureGroupTitle} icon={<HttpsOutlined />}>
                          <Grid container alignItems="flex-start" justify="space-between">
                            <Grid item>Secure Payment</Grid>
                            <Grid item>
                              <PoweredByStripe />
                            </Grid>
                          </Grid>
                        </GroupTitle>
                        <GroupContent>
                          <GroupItem wide>
                            <Input name="name" label="Cardholder Name" validate={required} />
                          </GroupItem>
                          <GroupItem wide>
                            <Stripe name="card" validate={requireCard} />
                          </GroupItem>
                          <GroupItem wide>
                            {submitError && <FormLabel error>{submitError}</FormLabel>}
                          </GroupItem>
                          <GroupItem wide>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color="primary"
                                  checked={pageType === ADD_NEW_CARD_PAGE ? true : saveCard}
                                  onChange={() => setSaveCard(!saveCard)}
                                  name="willSave"
                                  disabled={pageType === ADD_NEW_CARD_PAGE}
                                />
                              }
                              label="Save card details"
                            />
                          </GroupItem>
                          <GroupItem wide>
                            <Typography>
                              You can securely store your card details on Stripe to make future
                              checkouts faster. Saving a Card is required for Auto-Refill.
                            </Typography>
                          </GroupItem>
                        </GroupContent>
                      </Group>
                    </>
                  )}

                  {pageType !== ADD_BALANCE_PAGE && pageType !== ADD_NEW_CARD_PAGE && (
                    <GroupItem wide>
                      {submitError && <FormLabel error>{submitError}</FormLabel>}
                    </GroupItem>
                  )}
                </div>
              )}
            </>
            <Box className={classes.actions}>
              <Grid container spacing={4}>
                <Grid item xs={!settingsError ? 6 : 12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color={!settingsError ? 'secondary' : 'primary'}
                    size="large"
                    onClick={() => handleClose()}
                    className={classes.dialogButton}>
                    Cancel
                  </Button>
                </Grid>
                {!settingsError && (
                  <Grid item xs={6}>
                    <SubmitButton
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      label={pageType === SINGLE_PAYMENT_TAB ? 'Pay by Card' : 'Submit'}
                      type="submit"
                      disabled={
                        formSubmitting ||
                        (pageType === AUTO_REFILL_TAB && !pickedCard && !autoRefillCard) ||
                        feesLoading
                      }
                      submitting={formSubmitting}
                      uppercase={false}
                      progress={loading ? paymentLoader : undefined}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </form>
        );
      }}
    />
  );
};

export default Card;
